import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import React from 'react'

import { EXCEPTION_TAIL, EXCEPTION_TYPE, getExceptionTailKind } from 'settings/constants/exceptionType'
import T from 'T'
import { resolveExceptionContainerLinkId } from 'utils/exceptionContainer'
import { getExceptionDescriptionValue, getExceptionResponsibleParty, isRawMobileCode } from 'utils/exceptionFields'
import { get } from 'utils/lodash'

import ReadMoreTypography from '../ReadMoreTypography'

import ExceptionContainerChip from './ExceptionContainerChip'
import ExceptionMetaChips from './ExceptionMetaChips'
import ExceptionTailPill from './ExceptionTailPill'
import ExceptionTypeChip from './ExceptionTypeChip'

import DelayTail from './per-type/DelayTail'
import NoAccessTail from './per-type/NoAccessTail'

import type { WorkOrderContainer } from '../../containers/types'
import type { ExceptionShape } from './types'

// Card body: type pill + per-type tail (row 1), status/source/billed meta chips (row 2),
// container chip (row 3), then labeled Description and Note rows.
// Description carries per-type values that don't fit a tail; Note is strictly noteText.
// Coordinates render on the image overlay (ExceptionCardTopOverlay), not in the body.
type ExceptionCardBodyProps = {
  exception: ExceptionShape
  exceptionType?: string
  description?: string | null
  delayTime?: number | null
  note?: string | null
  serviceAction?: string | null
  containers?: WorkOrderContainer[]
}

const ReadMoreTypographyUntyped = ReadMoreTypography as React.ElementType

const toTrimmedString = (value: unknown): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

const toCoordinates = (latitude: unknown, longitude: unknown): string =>
  latitude != null && longitude != null ? `${latitude}, ${longitude}` : ''

// Shares utils/exceptionFields with the drawer prefill so the pill and the drawer always agree.
// The responsible-party guard stays for the window where this deploys ahead of the backend.
const buildDetailValue = (exception: ExceptionShape, exceptionType?: string): string => {
  if (exceptionType === EXCEPTION_TYPE.DAMAGE) {
    const damage = getExceptionDescriptionValue(exception)
    const responsible = getExceptionResponsibleParty(exception)
    return [damage, isRawMobileCode(responsible) ? '' : responsible].filter(Boolean).join(' · ')
  }
  if (
    exceptionType === EXCEPTION_TYPE.WEIGHT ||
    exceptionType === EXCEPTION_TYPE.SERIAL_NUMBER ||
    exceptionType === EXCEPTION_TYPE.SPECIAL ||
    exceptionType === EXCEPTION_TYPE.EXTRA_ITEM
  ) {
    return getExceptionDescriptionValue(exception)
  }
  return ''
}

const ExceptionCardBody: React.FC<ExceptionCardBodyProps> = ({
  exception,
  exceptionType,
  description,
  delayTime,
  note,
  serviceAction,
  containers = [],
}) => {
  // Embedded ContainerResponse when the payload joins it; pre-join payloads carry only a link id.
  const linkedContainerId = resolveExceptionContainerLinkId(exception)
  const container = get(exception, 'container') || (linkedContainerId ? containers.find(item => item.id === linkedContainerId) : undefined)
  const tailKind = getExceptionTailKind(exceptionType)
  const detailValue = buildDetailValue(exception, exceptionType)
  const isDescriptionConsumed =
    tailKind === EXCEPTION_TAIL.SUB_OPTION ||
    Boolean(detailValue) ||
    (tailKind === EXCEPTION_TAIL.DELAY && Boolean(delayTime && delayTime > 0))
  const displayNote = toTrimmedString(note)
  // Show `description` as its own row only for legacy free text — not a per-type value, and not
  // a mirror of the note (the web copies the note into `description` for the mobile app).
  const trimmedDescription = toTrimmedString(description)
  const legacyDescription = trimmedDescription && !isDescriptionConsumed && trimmedDescription !== displayNote ? trimmedDescription : ''
  const displayDescription = legacyDescription

  // Set location: the container's stored coordinates are where it was; the exception's
  // captured coordinates are where the driver placed it. Rendered around the container chip
  // per the design ("Previous location" in red, "Current location" below the chip).
  const isLocationType = exceptionType === EXCEPTION_TYPE.LOCATION
  const previousCoordinates = isLocationType ? toCoordinates(get(container, 'latitude'), get(container, 'longitude')) : ''
  const currentCoordinates = isLocationType ? toCoordinates(get(exception, 'latitude'), get(exception, 'longitude')) : ''

  return (
    <Grid container rowSpacing={1}>
      <Grid container alignItems="center" justifyContent="space-between" size={12}>
        <Grid size={{ xs: 12, md: 'grow' }}>
          <ExceptionTypeChip exceptionType={exceptionType} />
        </Grid>
        {(tailKind !== EXCEPTION_TAIL.NONE || detailValue) && (
          <Grid size={{ xs: 12, md: 'auto' }}>
            {tailKind === EXCEPTION_TAIL.DELAY && <DelayTail delayTime={delayTime} />}
            {tailKind === EXCEPTION_TAIL.SUB_OPTION && <NoAccessTail value={description} />}
            {tailKind === EXCEPTION_TAIL.NONE && detailValue && <ExceptionTailPill>{detailValue}</ExceptionTailPill>}
          </Grid>
        )}
      </Grid>

      {(exception.confirmationStatus || exception.status || exception.source || exception.billed != null) && (
        <Grid size={12}>
          <ExceptionMetaChips exception={exception} />
        </Grid>
      )}

      {previousCoordinates && (
        <Grid size={12}>
          <Typography color="error" fontWeight={600} variant="body2">
            {T.PREVIOUS_LOCATION}
          </Typography>
          <Typography fontWeight="normal" variant="h6">
            {previousCoordinates}
          </Typography>
        </Grid>
      )}

      <Grid size={12}>
        <ExceptionContainerChip container={container} serviceAction={serviceAction} />
      </Grid>

      {currentCoordinates && (
        <Grid size={12}>
          <Typography color="textSecondary" variant="body2">
            {T.CURRENT_LOCATION}
          </Typography>
          <Typography fontWeight="normal" variant="h6">
            {currentCoordinates}
          </Typography>
        </Grid>
      )}

      {displayDescription && (
        <Grid size={12}>
          <Typography color="textSecondary" variant="body2">
            {T.DESCRIPTION}
          </Typography>
          <ReadMoreTypographyUntyped fontWeight="normal" text={displayDescription} title={T.DESCRIPTION} variant="h6" />
        </Grid>
      )}

      {displayNote && (
        <Grid size={12}>
          <Typography color="textSecondary" variant="body2">
            {T.NOTE}
          </Typography>
          <ReadMoreTypographyUntyped fontWeight="normal" text={displayNote} title={T.NOTE} variant="h6" />
        </Grid>
      )}
    </Grid>
  )
}

export default ExceptionCardBody
